/**
 * Shift Application response types for frontend consumption.
 *
 * These types represent the API response structures for the shift
 * application system (employee self-assignment to open shift units).
 */

import type { ShiftApplicationStatus, ShiftApplicationType } from '../models/shift-application.model';

/**
 * Shift application entity as returned by the API.
 */
export interface ShiftApplication {
  /** Unique identifier for this application */
  id: string;
  /** The plan instance this application belongs to */
  planInstanceId: string;
  /** The shift unit the employee applied for */
  shiftUnitId: string;
  /** The date the application is for (YYYY-MM-DD) */
  date: string;
  /** The employee who applied */
  employeeId: string;
  /** Current status of the application */
  status: ShiftApplicationStatus;
  /** Creation timestamp (ISO 8601) */
  createdAt: string;
  /** Last update timestamp (ISO 8601) */
  updatedAt: string;
}

export const IneligibilityReason = {
  MISSING_SKILLS: 'missing_skills',
  MISSING_SKILL_RATING: 'missing_skill_rating',
  ABSENCE: 'absence',
  OVERLAP: 'overlap',
  NO_CAPACITY: 'no_capacity',
  ALREADY_APPLIED: 'already_applied',
  ALREADY_ASSIGNED: 'already_assigned',
  EXPIRED_SKILL: 'expired_skill',
  EMPLOYMENT_INACTIVE: 'employment_inactive',
} as const;
export type IneligibilityReason = (typeof IneligibilityReason)[keyof typeof IneligibilityReason];

/**
 * An open shift unit that an employee can potentially apply for.
 * Returned by the open shifts query endpoint.
 */
export interface OpenShiftUnit {
  /** Plan instance ID */
  planInstanceId: string;
  /** Plan name for display */
  planName: string;
  /** Team name for display */
  teamName: string;
  /** Team color for visual grouping */
  teamColor: string | null;
  /** Shift unit ID */
  shiftUnitId: string;
  /** Shift name for display */
  shiftName: string;
  /** Shift icon identifier */
  shiftIcon: string | null;
  /** Shift color for visual styling */
  shiftColor: string;
  /** Shift start time (HH:mm) */
  startTime: string;
  /** Shift end time (HH:mm) */
  endTime: string;
  /** The date this open slot is for (YYYY-MM-DD) */
  date: string;
  /** Number of available slots (capacity minus currently assigned) */
  availableSlots: number;
  /** Total capacity for this shift unit */
  totalCapacity: number;
  /** Skills required by this shift */
  requiredSkills: { id: string; name: string }[];
  /** Whether the current employee meets all eligibility requirements */
  eligible: boolean;
  /** If not eligible, the reason(s) why */
  ineligibilityReasons?: IneligibilityReason[];
  /** Whether the current employee is already assigned to this shift unit on this date (by a planner, not via self-application) */
  alreadyAssigned?: boolean;
  /** Whether the current employee self-applied for this shift unit on this date */
  alreadyApplied?: boolean;
  /** If already applied via the application system, the application ID (for withdraw) */
  applicationId?: string;

  // --- Swap/takeover fields ---
  /** The type of this open shift entry: application (planner-marked), swap, or takeover. */
  type: ShiftApplicationType;
  /** For swap/takeover: the employee who currently holds this shift. */
  assignedEmployeeId?: string;
  /** For swap/takeover: display name of the employee who holds this shift. */
  assignedEmployeeName?: string;
  /** Whether the current user already has a pending request for this shift. */
  hasPendingRequest?: boolean;
  /** If a pending request exists, the request ID (for cancel). */
  pendingRequestId?: string;
}

/**
 * Response for the "my applications" endpoint.
 * Includes shift details alongside the application record.
 */
export interface MyShiftApplication extends ShiftApplication {
  /** Shift name for display */
  shiftName: string;
  /** Shift color for visual styling */
  shiftColor: string;
  /** Shift start time (HH:mm) */
  startTime: string;
  /** Shift end time (HH:mm) */
  endTime: string;
  /** Team name for display */
  teamName: string;
  /** Plan name for display */
  planName: string;
}

// =============================================================================
// Shift Swap & Takeover Response Types
// =============================================================================

/**
 * One of the current employee's own assigned shifts, available to offer in a swap.
 * Returned by the `/shift-swaps/my-shifts` endpoint.
 */
export interface MyAssignedShift {
  /** Plan instance ID containing this assignment */
  planInstanceId: string;
  /** Plan name for display */
  planName: string;
  /** Team name for display */
  teamName: string;
  /** Shift unit ID */
  shiftUnitId: string;
  /** Shift name for display */
  shiftName: string;
  /** Shift color for visual styling */
  shiftColor: string;
  /** Shift start time (HH:mm) */
  startTime: string;
  /** Shift end time (HH:mm) */
  endTime: string;
  /** Effective location ID after employee overrides. */
  locationId?: string | null;
  /** Effective location display name after employee overrides. */
  locationName?: string | null;
  /** The date of this assignment (YYYY-MM-DD) */
  date: string;

  /**
   * Whether the target employee is eligible for this shift (skills + absences).
   * Only populated when `targetEmployeeId` is provided in the query.
   */
  targetEligible?: boolean;
  /**
   * Reason keys why the target employee is ineligible for this shift.
   * Only populated when `targetEmployeeId` is provided and `targetEligible` is false.
   */
  targetIneligibilityReasons?: string[];
}

/**
 * A swap or takeover request with display details for both parties.
 * Returned by the `/shift-swaps/requests` endpoint.
 */
export interface SwapRequest {
  /** Unique request ID */
  id: string;
  /** Whether this is a swap or takeover */
  type: ShiftApplicationType;
  /** Current status of the request */
  status: ShiftApplicationStatus;
  /** When the request was created (ISO 8601) */
  createdAt: string;
  /** When the request was last updated (ISO 8601) */
  updatedAt: string;

  // --- Requester info ---
  /** ID of the employee who initiated the request */
  requesterId: string;
  /** Display name of the requester */
  requesterName: string;

  // --- Target shift (the one the requester wants) ---
  /** Name of the target shift */
  targetShiftName: string;
  /** Color of the target shift */
  targetShiftColor: string;
  /** Start time of the target shift (HH:mm) */
  targetStartTime: string;
  /** End time of the target shift (HH:mm) */
  targetEndTime: string;
  /** Date of the target shift (YYYY-MM-DD) */
  targetDate: string;
  /** ID of the employee who holds the target shift */
  targetEmployeeId: string;
  /** Display name of the target shift holder */
  targetEmployeeName: string;
  /** Plan name for the target shift */
  targetPlanName: string;

  // --- Offered shift (swap only, undefined for takeover) ---
  /** Name of the offered shift */
  offeredShiftName?: string;
  /** Color of the offered shift */
  offeredShiftColor?: string;
  /** Start time of the offered shift (HH:mm) */
  offeredStartTime?: string;
  /** End time of the offered shift (HH:mm) */
  offeredEndTime?: string;
  /** Date of the offered shift (YYYY-MM-DD) */
  offeredDate?: string;
  /** Plan name for the offered shift */
  offeredPlanName?: string;
}
